/**
 * ⭐ F183 — NOT CONFORMING, on an inbound appointment (Decisions 341, 355, migration 236)
 *
 * > Pete's receiving SLA counts *"conforming inbound"* only; open item 96: *"a person's
 * > flag for spec / packaging / compliance"* → *"Build it now"*.
 *
 * Conforming is the normal case and shows as one quiet line. Marking a load not
 * conforming takes a reason (from the server's list) and an optional note; the load
 * is then left out of dock-to-stock with that reason. Every change is audited.
 *
 * ⚠️ A READ FAILURE HIDES THE PANEL rather than putting an error over the buttons a
 * scheduler came to this dialog to press (the paperwork panel's rule).
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
    getAppointmentConforming, setAppointmentConforming,
    type ConformingDto, type NonConformingReason,
} from '@/api/client';

export function ConformingPanel({ appointmentId, getToken, disabled }: {
    appointmentId: number;
    getToken: () => Promise<string>;
    disabled?: boolean;
}) {
    const [state, setState] = useState<ConformingDto | null>(null);
    const [editing, setEditing] = useState(false);
    const [reason, setReason] = useState<NonConformingReason | ''>('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        setState(null); setEditing(false); setError(null);
        (async () => {
            try {
                const r = await getAppointmentConforming(await getToken(), appointmentId);
                if (alive) setState(r);
            } catch { /* hidden: see the header note */ }
        })();
        return () => { alive = false; };
    }, [appointmentId, getToken]);

    if (!state || state.direction.toUpperCase() !== 'INBOUND') return null;
    const reasons = state.reasons ?? [];

    async function save(next: NonConformingReason | null) {
        setSaving(true); setError(null);
        try {
            const r = await setAppointmentConforming(await getToken(), appointmentId, next, next ? (note.trim() || null) : null);
            setState({ ...r, reasons: state?.reasons, noteMax: state?.noteMax });
            setEditing(false);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not save.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="col-span-2" data-conforming-panel="">
            <div className={cn('rounded-md border p-3 text-sm',
                state.reason ? 'border-amber-500/40 bg-amber-500/10' : 'border-border bg-muted/40')}>
                {!editing && (
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">
                            {state.reason ? `Not conforming — ${state.reason}` : 'Conforming load'}
                        </span>
                        {state.reason && state.note && <span className="text-xs">{state.note}</span>}
                        {state.reason && state.by && (
                            <span className="text-xs text-muted-foreground">
                                marked by {state.by}{state.atUtc ? ` · ${new Date(state.atUtc).toLocaleString()}` : ''}
                            </span>
                        )}
                        <span className="ml-auto flex gap-2">
                            {state.reason ? (
                                <Button variant="outline" size="sm" disabled={disabled || saving || !state.migrated}
                                        onClick={() => void save(null)}>
                                    Mark conforming
                                </Button>
                            ) : (
                                <Button variant="outline" size="sm" disabled={disabled || saving || !state.migrated}
                                        onClick={() => { setEditing(true); setReason(''); setNote(''); }}>
                                    Mark not conforming…
                                </Button>
                            )}
                        </span>
                    </div>
                )}
                {editing && (
                    <div className="space-y-2">
                        <div className="font-medium">Not conforming</div>
                        <p className="text-xs text-muted-foreground">
                            A load marked not conforming is left out of dock-to-stock, with this reason.
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                            <select value={reason} onChange={e => setReason(e.target.value as NonConformingReason | '')}
                                    className="rounded border border-gray-300 px-2 py-1 text-sm" disabled={saving}>
                                <option value="">Choose a reason…</option>
                                {reasons.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                            <input value={note} onChange={e => setNote(e.target.value)} maxLength={state.noteMax}
                                   placeholder="What was wrong (optional)" disabled={saving}
                                   className="min-w-[16rem] flex-1 rounded border border-gray-300 px-2 py-1 text-sm" />
                        </div>
                        <div className="flex gap-2">
                            <Button size="sm" disabled={saving || !reason} onClick={() => void save(reason || null)}>
                                {saving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outline" size="sm" disabled={saving} onClick={() => setEditing(false)}>Cancel</Button>
                        </div>
                    </div>
                )}
                {!state.migrated && (
                    <div className="mt-1.5 text-xs text-muted-foreground">
                        Migration 236 is not applied, so a load cannot be marked not conforming yet.
                    </div>
                )}
                {error && <div className="mt-1.5 text-xs text-rose-700">{error}</div>}
            </div>
        </div>
    );
}
